/**
 * Ported from the lekterion codebase (`src/lib/i18n.ts`) @ `d942f2f`. Bindery
 * is canonical for this module from 2026-09-06; reconcile source-side drift
 * toward this copy.
 */

/**
 * Reader target language for glosses/translations (§5+).
 *
 * The reader picks a target from the Settings menu; the choice is persisted by
 * `LanguageProvider` (`useTargetLang`) and flows into every gloss/translate call
 * AND the frozen cache key (`src/lib/model/hash.ts` + `convex/cacheKey.ts`), so
 * each language caches on its own row. `DEFAULT_TARGET_LANG` is the fallback when
 * no choice is stored yet. The server prompt names the language for the LLM and
 * maps the code in `convex/ai.ts` — keep `TARGET_LANGS` codes in sync with that
 * map.
 */
export type TargetLang = "en" | "es" | "fr" | "de" | "it" | "pt" | "zh" | "ja";

/** Selectable targets, labelled in their own language for the picker. */
export const TARGET_LANGS: { code: TargetLang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];

export const DEFAULT_TARGET_LANG: TargetLang = "en";

const CODES = new Set<string>(TARGET_LANGS.map((t) => t.code));

/** Narrow an arbitrary string to a supported target language, else null. */
export function asTargetLang(value: string | null): TargetLang | null {
  return value !== null && CODES.has(value) ? (value as TargetLang) : null;
}
